import React from 'react';
import Toggle from '../shared/Toggle';

// A HOTEL'S MCP-AS-A-SERVICE SETTINGS, AS GROUPS — one definition drawn by both the set-up wizard (one group per step)
// and the hotel's Settings tab (one card per group), so the two cannot offer different fields or defaults. The names
// are mcp_hotel_settings' columns, which PUT /pipelines/mcp-service/hotels/{hotelId} validates (HOTEL_FIELDS).

export const HOTEL_DEFAULTS = {
  service_status: 'ACTIVE', booking_enabled: 'N', cancel_enabled: 'N', hold_minutes: 15, max_nights: 14,
  max_age_minutes: 120, guarantee_code: '', payment_method_code: '', market_code: '', source_code: '', channel_code: '',
  return_url: '', reply_to_email: '', sender_name: '', uapi_credential_id: '', mcp_source_system_id: '',
  website_url: '', crawl_domains: '', crawl_weekly: 'N', direct_benefits: '', handover_email: '',
};

export const GROUPS = [
  { key: 'service', title: 'Service', hint: 'The PMS the hotel runs, and the Universal API credential the service acts as — the hotel’s availability, rates and bookings are read and made through it.' },
  { key: 'booking', title: 'Booking policy', hint: 'What assistants may do, and the limits on it.' },
  { key: 'opera', title: 'OPERA codes', hint: 'The codes every reservation an assistant makes is created with in OPERA.' },
  { key: 'emails', title: 'Guest emails', hint: 'Who guest emails come from and where replies go. The sending domain is on the Email sender tab.' },
  { key: 'content', title: 'Content', hint: 'Where the service reads the hotel’s own information, and what it says about booking direct.' },
];

/** The settings form as the API expects it (numbers as numbers, no credential as null). */
export const toPayload = (form) => ({
  ...form,
  hold_minutes: Number(form.hold_minutes), max_nights: Number(form.max_nights), max_age_minutes: Number(form.max_age_minutes),
  uapi_credential_id: form.uapi_credential_id ? Number(form.uapi_credential_id) : null,
  mcp_source_system_id: form.mcp_source_system_id ? Number(form.mcp_source_system_id) : null,
});

/** One group's fields. `hotel` is the console's hotel entry (its name, its Universal API credentials); `sources` the
 *  PMS systems the service supports (the console's `sources`). */
export function HotelFieldGroup({ group, form, setForm, edit, hotel, sources = [] }) {
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const yn = (k) => (on) => setForm(f => ({ ...f, [k]: on ? 'Y' : 'N' }));
  const text = (k, label, props = {}) => (
    <label>{label}<input value={form[k] ?? ''} onChange={set(k)} disabled={!edit} {...props} /></label>
  );
  const toggle = (k, label) => (
    <div><div className="mcps-meta">{label}</div><Toggle on={form[k] === 'Y'} onLabel="Yes" offLabel="No" disabled={!edit} onChange={yn(k)} /></div>
  );

  switch (group) {
    case 'service':
      return (
        <div className="mcps-grid">
          <label>PMS
            <select value={form.mcp_source_system_id || ''} onChange={set('mcp_source_system_id')} disabled={!edit}>
              <option value="">— not chosen —</option>
              {sources.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <label>Acts as (Universal API credential)
            <select value={form.uapi_credential_id || ''} onChange={set('uapi_credential_id')} disabled={!edit}>
              <option value="">— not running the service —</option>
              {(hotel.uapiCredentials || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          {!(hotel.uapiCredentials || []).length && (
            <p className="mcps-meta">No Universal API credential has a binding for this hotel yet — give one access on the hotel&rsquo;s
              Products › Universal API tab first.</p>
          )}
        </div>
      );
    case 'booking':
      return (
        <div className="mcps-grid">
          {toggle('booking_enabled', 'Agents may book')}
          {toggle('cancel_enabled', 'Agents may cancel')}
          {text('hold_minutes', 'Hold while paying (minutes)', { type: 'number', min: 5, max: 120 })}
          {text('max_nights', 'Longest stay (nights)', { type: 'number', min: 1, max: 60 })}
          {text('max_age_minutes', 'Stop search if availability is older than (minutes)', { type: 'number', min: 5 })}
        </div>
      );
    case 'opera':
      return (
        <div className="mcps-grid">
          {text('guarantee_code', 'Guarantee code')}
          {text('payment_method_code', 'Payment method code')}
          {text('market_code', 'Market code')}
          {text('source_code', 'Source code')}
          {text('channel_code', 'Channel (source of sale)')}
        </div>
      );
    case 'emails':
      return (
        <div className="mcps-grid">
          {text('sender_name', 'Email sender name', { placeholder: hotel.hotelName })}
          {text('reply_to_email', 'Guest replies go to', { type: 'email' })}
          {text('handover_email', 'Guest hand-overs go to', { type: 'email', placeholder: 'reception@yourhotel.com' })}
          {text('return_url', 'After payment, send the guest to', { placeholder: 'https://www.yourhotel.com' })}
        </div>
      );
    case 'content':
      return (
        <>
          <div className="mcps-grid">
            {text('website_url', 'Hotel website', { placeholder: 'https://www.yourhotel.com' })}
            {text('crawl_domains', 'Other sites to read (comma separated)', { placeholder: 'booking.yourhotel.com' })}
            {toggle('crawl_weekly', 'Re-read the website weekly')}
          </div>
          <label className="mcps-wide">Why book direct (shown with every price an assistant gives)
            <textarea rows={3} maxLength={1000} value={form.direct_benefits || ''} onChange={set('direct_benefits')} disabled={!edit}
                      placeholder="Best rate guaranteed · free breakfast for direct bookings · late check-out on request" /></label>
        </>
      );
    default:
      return null;
  }
}
